import type { Point2D, SeatRenderEntry, SeatTapIndex } from '../models/types';
import { SEAT_TAP_INDEX_CELL_SIZE } from '../constants/seatMap';

export const buildSeatTapIndex = (entries: SeatRenderEntry[]): SeatTapIndex => {
  const buckets = new Map<string, SeatRenderEntry[]>();

  entries.forEach(entry => {
    if (!entry.seat.isAvailable || !entry.seatInfo) return;

    const cellX = Math.floor(entry.seat.x / SEAT_TAP_INDEX_CELL_SIZE);
    const cellY = Math.floor(entry.seat.y / SEAT_TAP_INDEX_CELL_SIZE);
    const key = `${cellX}:${cellY}`;
    const bucket = buckets.get(key);

    if (bucket) {
      bucket.push(entry);
    } else {
      buckets.set(key, [entry]);
    }
  });

  return {
    cellSize: SEAT_TAP_INDEX_CELL_SIZE,
    buckets,
  };
};

export const findNearestSeatEntry = ({
  seatTapIndex,
  seatLayerPoint,
  nearestSeatRadius,
}: {
  seatTapIndex: SeatTapIndex;
  seatLayerPoint: Point2D;
  nearestSeatRadius: number;
}) => {
  const searchRadiusInCells = Math.max(1, Math.ceil(nearestSeatRadius / seatTapIndex.cellSize));
  const cellX = Math.floor(seatLayerPoint.x / seatTapIndex.cellSize);
  const cellY = Math.floor(seatLayerPoint.y / seatTapIndex.cellSize);
  let nearestEntry: SeatRenderEntry | null = null;
  let nearestDistance = nearestSeatRadius;

  for (let offsetX = -searchRadiusInCells; offsetX <= searchRadiusInCells; offsetX += 1) {
    for (let offsetY = -searchRadiusInCells; offsetY <= searchRadiusInCells; offsetY += 1) {
      const bucket = seatTapIndex.buckets.get(`${cellX + offsetX}:${cellY + offsetY}`);
      if (!bucket) continue;

      for (const entry of bucket) {
        const distance = Math.hypot(entry.seat.x - seatLayerPoint.x, entry.seat.y - seatLayerPoint.y);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestEntry = entry;
        }
      }
    }
  }

  return nearestEntry;
};
